/**
 * 運営者情報 (/about) ページ.
 * E-E-A-T 強化のため、長文プロフィール + 経歴タグ + JSON-LD 構造化データを含む.
 */

import React from 'react';
import { Factory, Cpu, Brain, Wrench, ShieldCheck, Sparkles, Building2, GraduationCap } from 'lucide-react';
import { PROFILE_DATA } from '../components/AuthorBox';

const SITE_URL = 'https://anzenpost.com';
const PERSON_ID = `${SITE_URL}/about#person`;
const ABOUT_URL = `${SITE_URL}/about`;
const IMAGE_URL = `${SITE_URL}/images/author-profile.png`;

// JSON-LD ProfilePage schema. SEO/E-E-A-T 上の正本.
const PROFILE_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'ProfilePage',
  mainEntity: {
    '@type': 'Person',
    '@id': PERSON_ID,
    name: PROFILE_DATA.name,
    alternateName: 'Ryota Kokubu',
    jobTitle: '制御設計エンジニア / AI・IoT・DX推進 / AIコンサルタント',
    description: '東京の製造業メーカーにて開発部門を担当。制御設計エンジニアを起点に、設備設計・改善プロジェクト・品質向上施策に従事。なぜなぜ分析（RCA）やリスクアセスメントを実務で活用し、IoT・AI・生成AI（RAG）を現場DXに実装。2025年より生成AIを活用したアプリの個人開発を開始。AIコンサルタントとして、製造業・建設業を中心に企業のAI・生成AI活用と現場DXの導入支援も行う。',
    url: ABOUT_URL,
    image: IMAGE_URL,
    sameAs: [
      PROFILE_DATA.socials.linkedin,
      PROFILE_DATA.socials.x,
      PROFILE_DATA.socials.note,
    ],
    worksFor: {
      '@type': 'Organization',
      name: '安全ポスト＋',
      url: SITE_URL,
    },
    knowsAbout: [
      '製造業DX', '建設業DX', 'IoT', '予兆保全', '異常検知', '生成AI', 'RAG',
      '電気制御設計', 'PLC', '安全管理', '品質管理', 'なぜなぜ分析', 'リスクアセスメント',
      '設備設計', '改善プロジェクト', 'データ分析', 'AIコンサルティング', '生成AI活用支援', 'AI導入支援',
      'ヒヤリハット', '4M分析', 'KY活動',
    ],
  },
};

interface AboutViewProps {
  onContact?: () => void;
}

export const AboutView: React.FC<AboutViewProps> = ({ onContact }) => {
  return (
    <>
      {/* JSON-LD 構造化データ. 検索エンジン向けの正本. */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(PROFILE_JSON_LD) }}
      />

      <div className="py-20 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-industrial-900 mb-3">運営者情報</h1>
          <p className="text-slate-500">About — 安全ポスト＋ 開発者プロフィール</p>
        </div>

        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden mb-10">
          <div className="md:flex">
            {/* Left */}
            <div className="md:w-1/3 bg-gradient-to-br from-blue-600 to-indigo-700 p-8 flex flex-col items-center justify-center text-white">
              <img
                src={PROFILE_DATA.imageUrl}
                alt={PROFILE_DATA.name}
                width={140}
                height={140}
                className="rounded-full ring-4 ring-white/30 ring-offset-2 ring-offset-blue-600 mb-4"
              />
              <h3 className="text-2xl font-bold">{PROFILE_DATA.name}</h3>
              <p className="text-blue-100 text-xs mt-1">Ryota Kokubu</p>
              <div className="mt-4 text-blue-100 text-sm text-center space-y-0.5">
                <p>制御設計エンジニア</p>
                <p>AI・IoT・DX推進</p>
                <p>AIコンサルタント</p>
              </div>
              <div className="flex gap-3 mt-6">
                <a href={PROFILE_DATA.socials.linkedin} target="_blank" rel="noopener" aria-label="LinkedIn" className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition">
                  <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
                <a href={PROFILE_DATA.socials.x} target="_blank" rel="noopener" aria-label="X (Twitter)" className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition">
                  <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
                <a href={PROFILE_DATA.socials.note} target="_blank" rel="noopener" aria-label="note" className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition">
                  <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M22.904 6.839l-3.743-3.744a3.672 3.672 0 00-5.192 0L2.658 14.406a1.488 1.488 0 00-.436 1.057v4.879a1.488 1.488 0 001.488 1.488h4.879c.395 0 .773-.157 1.052-.436L20.952 10.08a1.488 1.488 0 000-2.104l-1.095-1.094 3.047-3.047a.496.496 0 000-.701v.701l-.001.004z"/></svg>
                </a>
              </div>
            </div>

            {/* Right */}
            <div className="md:w-2/3 p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">経歴と専門性</h2>
              <p className="text-slate-700 leading-relaxed mb-6">
                東京の製造業メーカーにて開発部門を担当。制御設計エンジニアを起点に、設備設計・改善プロジェクト・品質向上施策に従事。なぜなぜ分析（RCA）やリスクアセスメントを実務で活用し、IoT・AI・生成AI（RAG）を現場DXに実装してきました。
              </p>
              <p className="text-slate-700 leading-relaxed mb-6">
                2025年より生成AIを活用したアプリの個人開発を開始。あわせて、AIコンサルタントとして、製造業・建設業を中心に企業のAI・生成AI活用と現場DXの導入支援も行っています。
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl">
                  <Factory className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-slate-800">重工業の制御設計出身</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
                  <Cpu className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-slate-800">IoT新規部門を立ち上げ</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-xl">
                  <Brain className="h-5 w-5 text-purple-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-slate-800">生成AI・RAGを現場実装</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-xl">
                  <Wrench className="h-5 w-5 text-orange-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-slate-800">RCA・リスクアセス実務経験</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 専門領域 */}
        <div className="bg-white rounded-2xl border border-slate-100 p-8 mb-10">
          <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-600" />
            専門領域
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-slate-500" />
                製造業・現場DX
              </h3>
              <ul className="text-sm text-slate-600 space-y-1.5 list-disc list-inside">
                <li>電気制御設計 / PLC</li>
                <li>設備設計・改善プロジェクト</li>
                <li>品質管理 / 改善活動</li>
                <li>なぜなぜ分析 (RCA)</li>
                <li>リスクアセスメント</li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-2">
                <Brain className="h-4 w-4 text-slate-500" />
                AI・IoT・データ活用
              </h3>
              <ul className="text-sm text-slate-600 space-y-1.5 list-disc list-inside">
                <li>IoT 予兆保全 / 異常検知</li>
                <li>生成AI / RAG の現場実装</li>
                <li>AIコンサルティング</li>
                <li>製造業・建設業向け DX 導入支援</li>
                <li>個人開発による現場ツール提供</li>
              </ul>
            </div>
          </div>
        </div>

        {/* このサービスについて */}
        <div className="bg-gradient-to-r from-orange-50 via-white to-yellow-50 rounded-2xl border border-orange-200/60 p-8 mb-10">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-orange-600" />
            安全ポスト＋について
          </h2>
          <p className="text-slate-700 leading-relaxed mb-4">
            安全ポスト＋ は、現場の「ヒヤリハット」を QRコードで30秒投稿し、AI が匿名化と4M分析を行い、改善実施までを追跡する SaaS です。
            建設業・製造業・物流業の現場で「報告書がまわらない」「ベテランの暗黙知が形式化されない」という課題を、AI と現場運用ノウハウの両面から解決したいと考えています。
          </p>
          <p className="text-slate-700 leading-relaxed">
            記事や機能は、私自身の製造現場での経験と、なぜなぜ分析・リスクアセスメント・IoT・AI 実装の実務知見をベースに作っています。コンテンツは個人の見解として責任を持って発信していますが、より深く話したい・自社に合わせて相談したい場合はお問い合わせください。
          </p>
        </div>

        {/* CTA */}
        <div className="bg-slate-900 rounded-2xl p-8 text-center">
          <h2 className="text-xl font-bold text-white mb-3 flex items-center justify-center gap-2">
            <GraduationCap className="h-5 w-5 text-yellow-400" />
            AI・生成AI活用 / DX導入のご相談も承ります
          </h2>
          <p className="text-slate-300 text-sm leading-relaxed mb-5 max-w-2xl mx-auto">
            安全ポスト＋ の導入相談はもちろん、より広く「製造業・建設業の現場での AI・生成AI 活用」「DX 推進」のコンサルティングのご相談もお受けしています。
          </p>
          {onContact && (
            <button
              onClick={onContact}
              className="bg-gradient-to-r from-safety-500 to-yellow-500 text-white px-8 py-3 rounded-xl font-bold hover:from-safety-600 hover:to-yellow-600 transition shadow-lg"
            >
              お問い合わせフォームへ →
            </button>
          )}
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-slate-400 text-center mt-10">
          {PROFILE_DATA.disclaimer}
        </p>
      </div>
    </>
  );
};
